// 환경 표면 재질(TSL, WebGPU·WebGL2 공용): 키트, 타일 바닥, 고원 풀밭, 절벽, 원경, 문짝.
// 환경은 손그림 텍스처를 그대로 살리고 외곽선을 쓰지 않는다(ART-BIBLE 3장). 림·툰은 캐릭터만.
import * as THREE from 'three/webgpu';
import {
  abs, asin, atan, cameraPosition, cameraProjectionMatrixInverse, cameraWorldMatrix, clamp, dFdx, dFdy, float, floor, fract, frontFacing,
  instanceColor, luminance, materialNormal, mix, mx_noise_float, normalize, normalWorld, positionGeometry, positionWorld, screenUV, select, smoothstep, step,
  sin, texture, transformNormalToView, uniform, uv, vec2, vec3, vec4,
} from 'three/tsl';
import { ditherKeep, kitCutNodes } from './cut';
import type { KitKind } from './layout';
import type { KitAsset } from './kits';
import type { KitRegion } from './regionKits';
import type { EnvGrade } from './mood';

type N = THREE.Node<'float'>;
/**
 * 마을 돌길 색(실시간 룩 조절판 "바닥 돌"이 이 값을 바꾼다). sat = 돌 텍스처 채도, tint = 돌 색 곱.
 * 목표 화면(dream-loop village target)의 돌은 회백 화강암에 늦은 햇빛이 따뜻하게 얹힌 색이다:
 * 돌 자체를 주황으로 칠하면 그늘까지 주황이 돼 "햇빛 받은 돌"이 아니라 "주황 돌"로 읽혔다.
 */
export const VILLAGE_FLOOR = { sat: uniform(0.62), tint: uniform(new THREE.Color().setHex(0xf8f0e8)) };
/** 유적 바닥 얼룩 햇빛: 잎 그늘 밝기(lo)와 햇빛 웅덩이 밝기(hi) */
const R1_DAPPLE = { lo: 0.7, hi: 1.14 };
/** 잘린 단면(뒷면) 색: 어두운 이끼 돌(돌 텍스처를 위에서 투영) */
const CAP_TINT = vec3(0.55, 0.6, 0.46);
/**
 * 지역판 환경(c023)의 표면 색 규칙. 지역1 유적 규칙(이끼 그늘·잎 그늘 햇빛·따뜻한 사암 곱)을 지역 색으로 바꾼다.
 * moss = 벽 밑 그늘 띠 색, dapple = 잎 그늘 얼룩 세기(0 = 흐린 날), tint = 바닥 최종 곱, ground = 고원 큰 얼룩 톤(낮·높),
 * cliff = 낭떠러지 색 곱, cap = 컷어웨이 단면 색, emit = 키트 발광 텍스처 배율
 */
export interface RegionLook {
  readonly moss: readonly [number, number, number];
  readonly mossK: number;
  readonly dapple: number;
  readonly tint: readonly [number, number, number];
  readonly groundLo: readonly [number, number, number];
  readonly groundHi: readonly [number, number, number];
  readonly cliff: readonly [number, number, number];
  readonly cap: readonly [number, number, number];
  readonly emit: number;
}
export const REGION_LOOK: Readonly<Record<KitRegion, RegionLook>> = {
  // 서리 묘지: 벽 밑에는 이끼 대신 몰린 눈(밝은 청백), 흐린 하늘이라 잎 그늘 없음, 바닥은 차갑게
  r3: { moss: [0.86, 0.92, 1.02], mossK: 0.5, dapple: 0, tint: [0.97, 1.0, 1.05], groundLo: [0.9, 0.94, 1.0], groundHi: [1.04, 1.05, 1.07], cliff: [0.62, 0.68, 0.8], cap: [0.42, 0.48, 0.6], emit: 1.5 },
  // 가라앉은 사원: 벽 밑은 짙은 이끼·물때, 안개 낀 흐린 빛이라 얼룩 햇빛을 약하게, 바닥은 녹회
  r4: { moss: [0.22, 0.34, 0.14], mossK: 0.55, dapple: 0.35, tint: [0.96, 1.0, 0.94], groundLo: [0.86, 0.92, 0.82], groundHi: [1.04, 1.06, 0.96], cliff: [0.6, 0.66, 0.56], cap: [0.4, 0.46, 0.34], emit: 1.7 },
  // 황금 모래 폐도(C2): 벽 밑에는 이끼 대신 몰린 금빛 모래, 사막이라 잎 그늘 없음, 바닥은 따뜻한 사암(그늘 청록은 분위기 분할 톤이 맡는다)
  r5: { moss: [1.0, 0.78, 0.46], mossK: 0.4, dapple: 0, tint: [1.03, 0.99, 0.92], groundLo: [0.92, 0.88, 0.8], groundHi: [1.06, 1.02, 0.94], cliff: [0.86, 0.72, 0.56], cap: [0.6, 0.46, 0.3], emit: 1.6 },
  // 심연의 성채(C2): 벽 밑은 보라 먼지 그늘, 하늘이 없어 얼룩 햇빛 없음, 바닥은 차가운 흑요석, 결정·룬·불꽃 발광은 r4 수준(벽 룬이 너무 밝으면 바닥 위험 칸 균열과 경쟁한다)
  r6: { moss: [0.3, 0.2, 0.46], mossK: 0.5, dapple: 0, tint: [0.98, 0.96, 1.06], groundLo: [0.86, 0.82, 0.96], groundHi: [1.06, 1.0, 1.14], cliff: [0.52, 0.46, 0.64], cap: [0.3, 0.24, 0.4], emit: 1.7 },
};

/** 카메라 쪽에서 잘릴 수 있는 종류(양면으로 그려 뒷면을 단면 뚜껑으로 쓴다) */
const CLIPPABLE: ReadonlySet<KitKind> = new Set<KitKind>(['wall', 'wall_broken', 'pillar', 'pillar_broken', 'arch', 'banner', 'brazier', 'crates', 'rubble', 'rock', 'rock_lo', 'stairs', 'mushrooms']);

/** 키트 재질: 텍스처 색 × 인스턴스 색, 카메라 쪽 컷(잘린 단면은 이끼 돌색), 디더, 종류별 발광 */
export function kitMaterial(a: KitAsset, kind: KitKind, capTex: THREE.Texture | null, ember = false, look: RegionLook | null = null): THREE.MeshStandardNodeMaterial {
  const clippable = CLIPPABLE.has(kind);
  const m = new THREE.MeshStandardNodeMaterial({ roughness: 0.92, metalness: 0, side: clippable ? THREE.DoubleSide : THREE.FrontSide });
  const cut = kitCutNodes();
  const source = a.map ? texture(a.map, uv()).rgb : vec3(0.62, 0.6, 0.55);
  // R2는 인스턴스와 재질에서 중복으로 검게 곱하지 않는다. 큰 지층 띠와 회청색 재를 남긴다.
  const strata = sin(positionWorld.y.mul(3.1).add(mx_noise_float(positionWorld.mul(0.38)).mul(1.4))).mul(0.5).add(0.5);
  const base = ember ? vec3(luminance(source).mul(0.7).add(0.12)).mul(mix(vec3(0.62, 0.65, 0.73), vec3(0.9, 0.79, 0.68), strata)) : source;
  const dither = ditherKeep(cut.dither as N);
  if (a.normalMap) {
    m.normalMap = a.normalMap;
    m.normalScale = new THREE.Vector2(0.75, 0.75);
  }
  if (clippable) {
    m.maskNode = cut.keep.and(dither);
    const capTint = look ? vec3(look.cap[0], look.cap[1], look.cap[2]) : CAP_TINT;
    const cap = capTex ? texture(capTex, positionWorld.xz.mul(0.3)).rgb.mul(ember ? vec3(0.3, 0.23, 0.23) : capTint) : vec3(0.3, 0.33, 0.22);
    m.colorNode = select(frontFacing, base, cap);
    // 뒷면(단면)은 위를 향한 평면처럼 비춘다(앞면은 노멀맵 그대로)
    m.normalNode = select(frontFacing, materialNormal, transformNormalToView(vec3(0, 1, 0)));
  } else {
    m.maskNode = dither;
    m.colorNode = base;
  }
  const b = base as unknown as ReturnType<typeof vec3>;
  // 마을 소품·건물: 늦은 오후 돌길에서 튀는 따뜻한 반사광(그늘 쪽이 새까맣게 죽지 않게)
  const bounce = kind.startsWith('village_') || kind === 'v_house' || kind === 'v_shop' || kind === 'v_forge' || kind === 'v_trainer' || kind === 'v_questboard' || kind === 'v_lamppost' ? 0.16 : 0;
  if (a.emissiveMap) {
    // 지역판 키트(c023): 발광 텍스처(얼음 결정 끝·포자 점·숯)를 그대로 쓴다. 앞면만(단면 뚜껑은 빛나지 않는다), 점멸 없음
    m.emissiveNode = texture(a.emissiveMap, uv()).rgb.mul(look ? look.emit : 1.3).mul(select(frontFacing, float(1), float(0)));
  } else if (kind === 'mushrooms') {
    // 푸른 갓만 청록으로 빛난다
    m.emissiveNode = b.mul(smoothstep(0.05, 0.22, b.z.sub(b.x))).mul(vec3(0.9, 2.3, 3.0)).mul(select(frontFacing, float(1), float(0)));
  } else if (kind === 'brazier') {
    m.emissiveNode = b.mul(smoothstep(0.3, 0.55, b.x.sub(b.z))).mul(smoothstep(0.45, 0.7, b.x)).mul(1.3).mul(select(frontFacing, float(1), float(0)));
  } else if (kind === 'v_gate') {
    const hot = instanceColor.r.sub(instanceColor.b).greaterThan(0.2);
    m.emissiveNode = b.mul(smoothstep(0.08, 0.25, b.z.sub(b.x))).mul(smoothstep(0.35, 0.6, b.y)).mul(select(hot, vec3(3.2, 0.35, 0.015), vec3(0.8, 2.0, 2.2)));
  } else if (kind === 'v_lamppost' || kind === 'v_house' || kind === 'v_shop') {
    // 등불·창: 밝고 따뜻한 화소만
    m.emissiveNode = b.mul(smoothstep(0.62, 0.85, luminance(b))).mul(smoothstep(0.18, 0.35, b.x.sub(b.z))).mul(kind === 'v_lamppost' ? 2.2 : 1.0).add(b.mul(vec3(1.0, 0.82, 0.6)).mul(bounce));
  } else if (bounce > 0) {
    m.emissiveNode = b.mul(vec3(1.0, 0.82, 0.6)).mul(bounce);
  }
  if (ember) {
    // 아래쪽 용암 반사광과 위쪽 하늘광. 정적 발광이므로 추가 광원·드로우콜·점멸이 없다.
    const low = float(1).sub(smoothstep(0, 3.2, positionWorld.y));
    const bounce = vec3(0.18, 0.038, 0.008).mul(low).add(vec3(0.025, 0.045, 0.075).mul(float(1).sub(low)));
    m.emissiveNode = ((m.emissiveNode ?? vec3(0)) as THREE.Node<'vec3'>).add(bounce.mul(luminance(source).add(0.25)));
  }
  return m;
}

/**
 * 반복 타일 표본: 타일 번호의 홀짝으로 좌우·상하를 번갈아 뒤집는다(거울 반복). 원화가 이음매 없는 그림이 아니어도
 * 이웃 타일이 늘 같은 가장자리 화소를 맞대므로 경계 선이 생기지 않는다(무작위 반전은 절반의 경계에서 끊겨 보였다).
 * 밝기 변주는 타일 단위 계단 대신 연속 노이즈로 준다. 연속 좌표의 미분으로 밉을 골라 경계에서 밉이 튀지 않게 한다.
 */
export function tiled(map: THREE.Texture, world: THREE.Node<'vec2'>, size: number, seed: number) {
  const c = world.div(size);
  const id = floor(c);
  const f = fract(c);
  const lu = mix(f.x, float(1).sub(f.x), step(0.25, fract(id.x.mul(0.5))));
  const lv = mix(f.y, float(1).sub(f.y), step(0.25, fract(id.y.mul(0.5))));
  const col = texture(map, vec2(lu, lv)).grad(dFdx(c), dFdy(c)).rgb;
  const n = mx_noise_float(vec3(c.mul(0.6), seed * 0.37)).mul(0.5).add(0.5);
  return { col, tint: mix(float(0.9), float(1.06), n) };
}

export interface FloorShadeTex {
  tex: THREE.DataTexture;
  originX: number;
  originZ: number;
  width: number;
  height: number;
}

/** 바닥: 큰 타일 + 타일별 변주 + 풀밭 얼룩 + 벽 밑 그늘·이끼. 손그림 원색이 햇빛에 주황으로 뜨지 않게 채도를 조금 낮춘다 */
export function floorMaterial(map: THREE.Texture, tile: number, shade: FloorShadeTex, warm: boolean, grass: THREE.Texture | null, grade: EnvGrade | null = null, look: RegionLook | null = null): THREE.MeshStandardNodeMaterial {
  const m = new THREE.MeshStandardNodeMaterial({ roughness: 0.95, metalness: 0 });
  const wp = positionWorld.xz;
  const t = tiled(map, wp, tile, 3);
  const macro = mx_noise_float(vec3(wp.mul(0.06), 1.7)).mul(0.5).add(0.5);
  // 월드 좌표의 넓은 얼룩을 타일 경계와 독립적으로 깔아 판석 반복을 끊는다.
  // 세 마스크 모두 저주파라 텍스처 샘플이나 추가 지오메트리가 필요 없다.
  const soil = smoothstep(0.42, 0.72, mx_noise_float(vec3(wp.mul(0.105), 12.4)).mul(0.5).add(0.5));
  const wet = smoothstep(0.58, 0.8, mx_noise_float(vec3(wp.mul(0.17), 19.1)).mul(0.5).add(0.5));
  const slab = mix(float(0.86), float(1.12), macro);
  const sUV = vec2(wp.x.sub(shade.originX).div(shade.width), wp.y.sub(shade.originZ).div(shade.height));
  const s = texture(shade.tex, sUV).r;
  let col = t.col.mul(t.tint).mul(slab);
  // 흙과 젖은 얼룩은 마을에서는 절제해 같은 함수가 쓰여도 따뜻한 돌길을 흐리지 않는다.
  const soilStrength = warm ? 0.1 : 0.22;
  const wetStrength = (warm ? 0.08 : 0.24) + (grade ? grade.wet : 0);
  col = mix(col, col.mul(warm ? vec3(0.86, 0.8, 0.68) : vec3(0.7, 0.76, 0.66)), soil.mul(soilStrength));
  col = mix(col, col.mul(warm ? vec3(0.88, 0.9, 0.86) : vec3(0.58, 0.68, 0.7)), wet.mul(wetStrength));
  if (warm) {
    // 마을 돌길: 돌마다 색이 조금씩 다르고(따뜻/차가운 고주파 얼룩) 줄눈은 더 어둡게 — 평평한 한 장처럼 보이지 않게
    const hue = mx_noise_float(vec3(wp.mul(1.9), 5.3));
    col = col.mul(vec3(float(1).add(hue.mul(0.07)), float(1), float(1).sub(hue.mul(0.07))));
    col = col.mul(mix(float(0.72), float(1.06), smoothstep(0.18, 0.55, luminance(t.col))));
  } else {
    // 유적 바닥: 판석마다 색 차이, 줄눈·금은 더 어둡게, 젖은 얼룩(어둡고 차갑게) — 같은 타일 반복으로 보이지 않게
    const hue = mx_noise_float(vec3(wp.mul(0.9), 8.1));
    col = col.mul(vec3(float(1).add(hue.mul(0.08)), float(1).add(hue.mul(0.02)), float(1).sub(hue.mul(0.06))));
    col = col.mul(mix(float(0.62), float(1.08), smoothstep(0.16, 0.5, luminance(t.col))));
    // 숲 지붕 사이로 떨어지는 얼룩 햇빛(콘셉트 r1-dungeon·dream-loop 던전 목표의 "나뭇잎 그늘 + 햇빛 웅덩이"):
    // 두 옥타브 잡음으로 밝은 웅덩이와 잎 그늘을 나눈다. 그림자 맵과 별개라 비용은 잡음 두 번뿐이다
    const leaf = mx_noise_float(vec3(wp.mul(0.21), 6.2)).mul(0.65).add(mx_noise_float(vec3(wp.mul(0.67), 2.9)).mul(0.35)).mul(0.5).add(0.5);
    const dapple = mix(float(R1_DAPPLE.lo), float(R1_DAPPLE.hi), smoothstep(0.38, 0.62, leaf));
    col = col.mul(look ? mix(float(1), dapple, look.dapple) : dapple);
  }
  // 마을 돌길은 꿀빛 사암(콘셉트 style-B-village): 채도를 거의 살리고 따뜻하게 민다
  // 던전 돌은 따뜻한 사암(콘셉트 r1-dungeon): 녹회색으로 죽지 않게 채도를 살리고 살짝 따뜻하게
  col = warm
    ? mix(vec3(luminance(col)), col, VILLAGE_FLOOR.sat).mul(VILLAGE_FLOOR.tint)
    : mix(vec3(luminance(col)), col, 0.82).mul(look ? vec3(look.tint[0], look.tint[1], look.tint[2]) : vec3(1.03, 0.98, 0.9));
  if (grass) {
    const g = tiled(grass, wp, 5, 21).col;
    const pn = mx_noise_float(vec3(wp.mul(0.13), 7.1)).mul(0.5).add(0.5);
    const fine = mx_noise_float(vec3(wp.mul(0.55), 2.3)).mul(0.12);
    // 풀은 어두운 줄눈부터 차오른다(돌 밝기가 낮을수록 먼저)
    const gap = float(0.45).sub(luminance(t.col)).max(0).mul(0.6);
    const patch = smoothstep(0.7, 0.78, pn.add(s.mul(0.3)).add(gap).add(fine)).mul(warm ? 0.3 : 0.6);
    col = mix(col, g.mul(0.92), patch);
  }
  const moss = warm ? vec3(0.55, 0.6, 0.35) : look ? vec3(look.moss[0], look.moss[1], look.moss[2]) : vec3(0.3, 0.42, 0.18);
  // 지역판 서리(r3)는 밝은 눈이라 바닥 밝기에 덜 묶는다(어두운 판석 위에서도 눈 띠가 하얗게 남는다)
  const mossCol = look && look.moss[2] > 0.6 ? moss.mul(luminance(col).mul(0.5).add(0.55)) : moss.mul(luminance(col).mul(1.6));
  col = mix(col, mossCol, s.mul(warm ? 0.25 : look ? look.mossK : 0.45).add(smoothstep(0.62, 0.9, macro).mul(warm ? 0.05 : 0.14)));
  // 지역·마을 색 보정(D3a): 채도·색 곱, 서리 얼룩(벽에서 먼 곳부터 하얗게 덮인다)
  if (grade) {
    col = mix(vec3(luminance(col)), col, grade.floorSat).mul(vec3(grade.floor[0], grade.floor[1], grade.floor[2]));
    if (grade.snow > 0) {
      const frost = smoothstep(0.46, 0.7, mx_noise_float(vec3(wp.mul(0.19), 31.7)).mul(0.5).add(0.5).add(mx_noise_float(vec3(wp.mul(0.8), 4.4)).mul(0.08)));
      col = mix(col, vec3(0.86, 0.92, 1.0).mul(luminance(col).mul(0.5).add(0.62)), frost.mul(grade.snow).mul(float(1).sub(s.mul(0.7))));
    }
  }
  // 벽 밑 그늘(던전은 깊게, 마을은 옅게)
  col = col.mul(float(1).sub(s.mul(warm ? 0.3 : 0.55)));
  m.colorNode = col;
  return m;
}

/** 고원 풀밭: 6m 타일 + 반전 변주 + 큰 색 얼룩, 낭떠러지 가장자리는 정점 색으로 어둡게 */
/**
 * 분수 둘레 나침반 상감(콘셉트 style-B-village·v-overview 바닥 문양): 정점 색(밝은 사암/청회 슬레이트)에
 * 바닥과 같은 돌 텍스처 무늬를 곱해 돌을 박아 넣은 것처럼 보이게 한다. 바닥보다 살짝 위, 깊이 오프셋으로 깜빡임을 막는다.
 */
export function inlayMaterial(map: THREE.Texture | null): THREE.MeshStandardNodeMaterial {
  const m = new THREE.MeshStandardNodeMaterial({ roughness: 0.9, metalness: 0, vertexColors: true, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
  const wp = positionWorld.xz;
  const stone = map ? tiled(map, wp, 4, 3).col : vec3(0.8, 0.75, 0.65);
  // 돌 무늬의 밝기만 빌린다(색은 정점 색)
  m.colorNode = vec3(luminance(stone).mul(1.0).add(0.05));
  return m;
}

export function groundMaterial(map: THREE.Texture | null, warm: boolean, grade: EnvGrade | null = null, look: RegionLook | null = null): THREE.MeshStandardNodeMaterial {
  const m = new THREE.MeshStandardNodeMaterial({ roughness: 1, metalness: 0, vertexColors: true });
  const wp = positionWorld.xz;
  const macro = mx_noise_float(vec3(wp.mul(0.045), 4.2)).mul(0.5).add(0.5);
  const tone = look
    ? mix(vec3(look.groundLo[0], look.groundLo[1], look.groundLo[2]), vec3(look.groundHi[0], look.groundHi[1], look.groundHi[2]), macro)
    : mix(vec3(0.86, 0.95, 0.72), warm ? vec3(1.08, 1.04, 0.86) : vec3(1.1, 1.02, 0.78), macro);
  let col = map ? (() => { const t = tiled(map, wp, 6, 11); return t.col.mul(t.tint).mul(tone); })() : vec3(0.36, 0.5, 0.22).mul(tone);
  // 지역 색 보정(D3a): 서리 묘지는 눈 덮인 풀밭, 늪 사원은 짙은 이끼
  if (grade) {
    col = mix(vec3(luminance(col)), col, grade.floorSat).mul(vec3(grade.floor[0], grade.floor[1], grade.floor[2]));
    if (grade.snow > 0) col = mix(col, vec3(0.9, 0.94, 1.0), smoothstep(0.3, 0.62, macro).mul(grade.snow));
  }
  m.colorNode = col;
  return m;
}

/** 낭떠러지 옆면: 돌 텍스처를 옆으로 감고 아래로 갈수록 안개색으로 사라진다 */
export function cliffMaterial(map: THREE.Texture | null, haze: THREE.Color, look: RegionLook | null = null): THREE.MeshStandardNodeMaterial {
  const m = new THREE.MeshStandardNodeMaterial({ roughness: 1, metalness: 0 });
  const wp = positionWorld;
  const along = wp.x.add(wp.z);
  const rock = map ? texture(map, vec2(along.mul(0.23), wp.y.mul(0.23))).rgb : vec3(0.5, 0.47, 0.42);
  const depth = float(1).sub(smoothstep(-9, -0.5, wp.y));
  const col = rock.mul(look ? vec3(look.cliff[0], look.cliff[1], look.cliff[2]) : vec3(0.82, 0.78, 0.72)).mul(mix(float(1), float(0.55), depth));
  const fade = depth.mul(0.9);
  m.colorNode = col.mul(float(1).sub(fade));
  m.emissiveNode = vec3(haze.r, haze.g, haze.b).mul(fade).mul(0.9);
  return m;
}

/**
 * 원경: 화면 전체 사각형을 가장 먼저 그린다(깊이 쓰기 없음). 무한히 먼 원통에 파노라마를 감은 것처럼
 * 화소마다 시선 방향(카메라 행렬로 복원)의 고도·방위로 표본을 고른다. 그래서 카메라 피치를 바꾸면 보이는 띠도 맞게 바뀐다.
 * 높은 고원 위에 있다는 설정이라 파노라마의 지평선을 화면 위 끝보다 aboveTopDeg만큼 위에 둔다: 화면 위 띠에는
 * 지평선 바로 아래(성채·폭포·절벽 줄)가 보인다. 화면 위 끝 고도는 카메라에서 바로 계산하므로 피치를 바꿔도 맞는다.
 * 좌우는 거울 반복으로 이음매를 숨기고, 방위는 카메라 x 이동에 느린 시차를 준다.
 */
export function backdropMaterial(map: THREE.Texture | null, haze: THREE.Color, aboveTopDeg: number, vHorizon: number, spanDeg: number): THREE.MeshBasicNodeMaterial {
  const m = new THREE.MeshBasicNodeMaterial({ depthWrite: false, depthTest: false, fog: false });
  m.vertexNode = vec4(positionGeometry.x, positionGeometry.y, 0.5, 1);
  const sv = screenUV.y; // 0 = 화면 위
  const rayDir = (x: N, y: N) => normalize(cameraWorldMatrix.mul(vec4(cameraProjectionMatrixInverse.mul(vec4(x, y, 1, 1)).xyz, 0)).xyz);
  const dir = rayDir(screenUV.x.mul(2).sub(1) as N, float(1).sub(sv.mul(2)) as N);
  const top = rayDir(float(0), float(1));
  const elevDeg = asin(clamp(dir.y, -1, 1)).mul(180 / Math.PI);
  const horizonDeg = asin(clamp(top.y, -1, 1)).mul(180 / Math.PI).add(aboveTopDeg);
  const azDeg = atan(dir.x, dir.z.negate()).mul(180 / Math.PI);
  // 이미지 세로: 지평선(vHorizon)에서 1도당 1/64(위로 하늘, 아래로 절벽·구름)
  const v = clamp(float(vHorizon).add(elevDeg.sub(horizonDeg).div(64)), 0.005, 0.995);
  const u = float(0.5).add(azDeg.div(spanDeg)).add(cameraPosition.x.mul(0.003));
  const h = vec3(haze.r, haze.g, haze.b);
  const img = map ? texture(map, vec2(u, v)).rgb : h;
  // 지평선에 가까울수록(먼 곳일수록) 공기 원근 안개가 짙다
  const far = float(1).sub(smoothstep(0, 30, abs(elevDeg.sub(horizonDeg))));
  // 원경 안개: 0.18+0.3이면 보스방 뒤 폭포·성채가 물빠진 회색으로 떠 보였다(dream-loop 보스 1~2회차)
  m.colorNode = mix(img, h, float(0.1).add(far.mul(0.24)));
  return m;
}

/** 문짝: 이끼 돌판(월드 좌표 텍스처라 높이가 줄어도 무늬가 찌그러지지 않는다) + 청록 봉인선 */
export function gateMaterial(map: THREE.Texture | null, ember = false, tone: 'stone' | 'wood' | 'bark' = 'stone'): THREE.MeshStandardNodeMaterial {
  const m = new THREE.MeshStandardNodeMaterial({ roughness: 0.9, metalness: 0 });
  const wp = positionWorld;
  // 옆면은 (수평, 높이), 윗면(컷으로 낮아졌을 때 보인다)은 xz로 투영해 늘어진 줄무늬를 막는다
  const side = vec2(wp.x.add(wp.z), wp.y);
  const top = wp.xz;
  const st = select(abs(normalWorld.y).greaterThan(0.5), top, side).mul(0.3);
  const stone = map ? texture(map, st).rgb : vec3(0.5, 0.48, 0.42);
  // 아래는 이끼, 가운데 높이에 가는 청록 봉인선 하나
  const mossy = float(1).sub(smoothstep(0.2, 1.4, wp.y));
  // 스테이지 지도: 고블린 진지는 거친 목재(붉은 갈색), 고목 성소는 어두운 껍질(청록 봉인선은 그대로)
  const base = tone === 'wood' ? vec3(0.5, 0.33, 0.2) : tone === 'bark' ? vec3(0.34, 0.27, 0.18) : vec3(0.66, 0.64, 0.6);
  m.colorNode = ember ? vec3(luminance(stone)).mul(vec3(0.25, 0.21, 0.23)) : mix(stone.mul(base), vec3(0.24, 0.34, 0.16), mossy.mul(tone === 'stone' ? 0.6 : 0.3));
  const band = float(1).sub(smoothstep(0.02, 0.06, abs(wp.y.sub(1.7))));
  m.emissiveNode = (ember ? vec3(1.5, 0.12, 0.01) : vec3(0.2, 0.75, 0.85)).mul(band.mul(0.6));
  return m;
}
